import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { useSelector } from 'react-redux';
import Login from '../../auth/Login';

function TopNavBar() {
    const user = useSelector(state => state.auth.user);
    const location = useLocation();

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/';
        }
        return location.pathname.startsWith(path);
    };

    return (
        <div className='no-print'>
            <AppBar position="fixed">
                <Container maxWidth="xl">
                    <Toolbar disableGutters sx={{ height: 64 }}>
                        {/* Logo with beta label */}
                        <Box sx={{ position: 'relative', mr: 2 }}>
                            <Box
                                component={Link}
                                to="/"
                                sx={{
                                    display: 'block',
                                    cursor: 'pointer'
                                }}
                            >
                                <Box
                                    component="img"
                                    src="/icon-no-bg.svg"
                                    sx={{
                                        width: 54,
                                        height: 54
                                    }}
                                />
                            </Box>
                            <Typography
                                component={Link}
                                to="/help"
                                variant="caption"
                                sx={{
                                    position: 'absolute',
                                    bottom: 18,
                                    right: -10,
                                    fontSize: '8px',
                                    fontWeight: 'bold',
                                    color: '#FF5722',
                                    backgroundColor: 'white',
                                    borderRadius: '2px',
                                    padding: '1px 2px',
                                    lineHeight: 1,
                                    zIndex: 1,
                                    textDecoration: 'none',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#FF5722',
                                        color: 'white',
                                        transform: 'scale(1.1)'
                                    }
                                }}
                            >
                                BETA
                            </Typography>
                        </Box>

                        {/* Desktop title */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', justifyContent: 'center', mr: 2 }}>
                            <Typography
                                variant="h4"
                                noWrap
                                component={Link}
                                to="/"
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: '.3rem',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    lineHeight: 1,
                                    mb: 0.3,
                                    fontSize: '1.75rem'
                                }}
                            >
                                CueMeUp
                            </Typography>
                            {location.pathname !== '/' && (
                                <Typography
                                    variant="caption"
                                    component="a"
                                    href="https://acier.cc"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        fontSize: '11px',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        textDecoration: 'none',
                                        lineHeight: 1,
                                        '&:hover': {
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    in partnership with <strong>Acier.cc</strong>
                                </Typography>
                            )}
                        </Box>

                        {/* Mobile title */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', justifyContent: 'center', flexGrow: 1, mr: 2 }}>
                            <Typography
                                variant="h4"
                                noWrap
                                component={Link}
                                to={"/"}
                                sx={{
                                    fontWeight: 700,
                                    letterSpacing: '.2rem',
                                    color: 'inherit',
                                    textDecoration: 'none',
                                    lineHeight: 1,
                                    mb: 0.2,
                                    fontSize: '1.4rem'
                                }}
                            >
                                CueMeUp
                            </Typography>
                            {location.pathname !== '/' && (
                                <Typography
                                    variant="caption"
                                    component="a"
                                    href="https://acier.cc"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        fontSize: '10px',
                                        color: 'rgba(255, 255, 255, 0.7)',
                                        textDecoration: 'none',
                                        lineHeight: 1,
                                        '&:hover': {
                                            color: 'rgba(255, 255, 255, 0.9)',
                                            textDecoration: 'underline'
                                        }
                                    }}
                                >
                                    in partnership with <strong>Acier.cc</strong>
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }} />

                        {/* Desktop Navigation Links */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, mr: 3 }}>
                            <Button
                                component={Link}
                                to="/"
                                color="inherit"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: isActive('/') ? 600 : 500,
                                    backgroundColor: isActive('/') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                Home
                            </Button>
                            {!user && (
                                <Button
                                    component={Link}
                                    to="/pricing"
                                    color="inherit"
                                    sx={{
                                        textTransform: 'none',
                                        fontWeight: isActive('/pricing') ? 600 : 500,
                                        backgroundColor: isActive('/pricing') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                        }
                                    }}
                                >
                                    Pricing
                                </Button>
                            )}
                            <Button
                                component={Link}
                                to="/help"
                                color="inherit"
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: isActive('/help') ? 600 : 500,
                                    backgroundColor: isActive('/help') ? 'rgba(255, 255, 255, 0.15)' : 'transparent',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                    }
                                }}
                            >
                                Help
                            </Button>
                        </Box>

                        <Login />
                    </Toolbar>
                </Container>
            </AppBar>
        </div>
    );
}

export default TopNavBar;
